/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import React from 'react';
import { colourGroups, getNameInitials, getCharacterGroupAndColour, getNextGroupIndex } from 'services/utils/avatar';

interface Props {
  profileId: string | number;
  name: string;
  className?: string;
}

export default function ProfileAvatar({ profileId, name, className }: Props): JSX.Element {
  const generateAvatar = (userName: string) => {
    const result = {
      // default colours
      firstColour: colourGroups[0].colour,
      secondColour: colourGroups[1].colour,
    };

    const userInitials = getNameInitials(userName);

    let firstCharacter;
    let secondCharacter;
    let nextIndexGroup;

    if (userInitials.length === 1) {
      firstCharacter = getCharacterGroupAndColour(userInitials.charCodeAt(0));
      if (Object.entries({}).toString() !== Object.entries(firstCharacter).toString()) {
        result.firstColour = firstCharacter.groupColour;

        // name has only 1 character, so we need to calculate the second colour
        // taking into account the calculated group index of the first character
        if (userName.length === 1) {
          nextIndexGroup = getNextGroupIndex(firstCharacter.groupIndex + 1);
          result.secondColour = colourGroups[nextIndexGroup].colour; // gets next group colour
        } else if (userName.length > 1) {
          // name has at least 2 characters
          secondCharacter = getCharacterGroupAndColour(userName[1].charCodeAt(0));

          if (Object.entries({}).toString() !== Object.entries(secondCharacter).toString()) {
            result.secondColour = colourGroups[secondCharacter.groupIndex].colour;
          } else {
            // fallback, should not happen, but check it anyway
            nextIndexGroup = getNextGroupIndex(firstCharacter.groupIndex + 1);
            result.secondColour = colourGroups[nextIndexGroup].colour;
          }
        }
      }
    } else if (userInitials.length === 2) {
      firstCharacter = getCharacterGroupAndColour(userInitials.charCodeAt(0));
      secondCharacter = getCharacterGroupAndColour(userInitials.charCodeAt(1));

      if (
        Object.entries(firstCharacter).toString() !== Object.entries({}).toString() &&
        Object.entries({}).toString() !== Object.entries(secondCharacter).toString()
      ) {
        result.firstColour = firstCharacter.groupColour;
        result.secondColour = colourGroups[secondCharacter.groupIndex].colour;
      }
    }

    return result;
  };

  const avatarData = generateAvatar(name);

  return (
    <span
      id={`ProfileAvatar___${profileId}`}
      className={`avatar ${className || ''}`}
      style={{ background: `linear-gradient(${avatarData.firstColour}, ${avatarData.secondColour})` }}
    >
      {getNameInitials(name)}
    </span>
  );
}
